<template>
  <form @submit.prevent="confirmPause" ref="pauseSensorEventForm">
    <div class="modal" tabindex="-1" role="dialog" backdrop="static" id="pauseSensorEventModal" @keyup.esc="onEsc($event)">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header header">
            <h3 class="modal-title">{{ $t('pauseSensorEventModal.title') }}</h3>
            <button type="button" class="btn-close btn-close-white" @click="close" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="form">
              <div v-show="errorMessage" class="alert alert-danger failed">{{ errorMessage }}</div>

              <div class="form-group" v-if="selectedSensorEvent">
                <label>{{ $t('pauseSensorEventModal.rule') }}</label>
                <div class="rule-summary">
                  {{ selectedSensorEvent.message || '#' + selectedSensorEvent.id }}
                </div>
              </div>

              <div class="form-group" v-if="isCurrentlyPaused">
                <div class="alert alert-info paused-summary">
                  {{ $t('pauseSensorEventModal.currentlyPaused') }}
                  <div class="paused-details">
                    <div><strong>{{ $t('pauseSensorEventModal.pausedUntil') }}:</strong> {{ formatTimestamp(selectedSensorEvent.pauseduntil) }}</div>
                    <div v-if="selectedSensorEvent.pausedbyusername"><strong>{{ $t('pauseSensorEventModal.pausedBy') }}:</strong> {{ selectedSensorEvent.pausedbyusername }}</div>
                    <div v-if="selectedSensorEvent.pausereason">{{ selectedSensorEvent.pausereason }}</div>
                  </div>
                </div>
              </div>

              <div class="form-group" v-if="!isCurrentlyPaused">
                <label>{{ $t('pauseSensorEventModal.presetTitle') }}</label>
                <div class="preset-bar">
                  <button type="button" class="btn btn-outline-info btn-sm" @click="applyPreset('midnight', $event)">{{ $t('pauseSensorEventModal.presetUntilMidnight') }}</button>
                  <button type="button" class="btn btn-outline-info btn-sm" @click="applyPreset('tomorrow13', $event)">{{ $t('pauseSensorEventModal.presetUntilTomorrow13') }}</button>
                  <button type="button" class="btn btn-outline-info btn-sm" @click="applyPreset('plus1h', $event)">{{ $t('pauseSensorEventModal.preset1Hour') }}</button>
                  <button type="button" class="btn btn-outline-info btn-sm" @click="applyPreset('plus24h', $event)">{{ $t('pauseSensorEventModal.preset24Hours') }}</button>
                </div>
              </div>

              <div class="form-group" v-if="!isCurrentlyPaused">
                <label for="pausedUntilInput">{{ $t('pauseSensorEventModal.pausedUntilInput') }}</label>
                <input type="datetime-local" class="form-control" id="pausedUntilInput" v-model="pausedUntil" />
                <div class="invalid-feedback d-block" v-if="showUntilError">{{ $t('pauseSensorEventModal.pausedUntilRequired') }}</div>
              </div>

              <div class="form-group" v-if="!isCurrentlyPaused">
                <label for="reasonInput">{{ $t('pauseSensorEventModal.reasonInput') }}</label>
                <input type="text" class="form-control" id="reasonInput" v-model.trim="reason" :placeholder="$t('pauseSensorEventModal.reasonPlaceholder')" maxlength="1024" />
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="submit" class="btn btn-primary" v-if="!isCurrentlyPaused">
              <font-awesome-icon icon="pause" />&nbsp; {{ $t('pauseSensorEventModal.pauseButton') }}
            </button>
            <button type="button" class="btn btn-warning" v-if="isCurrentlyPaused" @click="confirmUnpause">
              <font-awesome-icon icon="play" />&nbsp; {{ $t('pauseSensorEventModal.unpauseButton') }}
            </button>
            <button type="button" class="btn btn-secondary" @click="close">{{ $t('pauseSensorEventModal.close') }}</button>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { Modal } from 'bootstrap'
import cdcsensorService from '@/services/cdcsensors'
import ModalViewMixin from '@/mixins/ModalViewMixin.js'

export default {
  name: 'PauseSensorEventModal',
  mixins: [ModalViewMixin],
  props: {
    selectedSensorEvent: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      pausedUntil: '',
      reason: '',
      errorMessage: '',
      showUntilError: false
    }
  },
  computed: {
    isCurrentlyPaused () {
      const until = this.selectedSensorEvent && this.selectedSensorEvent.pauseduntil
      if (!until) return false
      return new Date(until).getTime() > Date.now()
    }
  },
  watch: {
    modalOpened () {
      this.errorMessage = ''
      this.showUntilError = false
      this.reason = ''
      this.pausedUntil = this.defaultPauseUntil()
    }
  },
  methods: {
    defaultPauseUntil () {
      // default = midnight (start of next day, local)
      const d = new Date()
      d.setDate(d.getDate() + 1)
      d.setHours(0, 0, 0, 0)
      return this.toLocalInputString(d)
    },
    toLocalInputString (date) {
      const pad = (n) => String(n).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
    },
    applyPreset (key, event) {
      if (event) event.preventDefault()
      const now = new Date()
      const target = new Date(now)
      switch (key) {
        case 'midnight':
          target.setDate(target.getDate() + 1)
          target.setHours(0, 0, 0, 0)
          break
        case 'tomorrow13':
          target.setDate(target.getDate() + 1)
          target.setHours(13, 0, 0, 0)
          break
        case 'plus1h':
          target.setHours(target.getHours() + 1)
          break
        case 'plus24h':
          target.setHours(target.getHours() + 24)
          break
      }
      this.pausedUntil = this.toLocalInputString(target)
      this.showUntilError = false
    },
    formatTimestamp (ts) {
      if (!ts) return ''
      try {
        return new Date(ts).toLocaleString()
      } catch (_e) {
        return ts
      }
    },
    confirmPause () {
      this.errorMessage = ''
      if (!this.pausedUntil) {
        this.showUntilError = true
        return
      }
      const target = new Date(this.pausedUntil)
      if (isNaN(target.getTime()) || target.getTime() <= Date.now()) {
        this.showUntilError = true
        return
      }
      cdcsensorService.pauseSensorEvent(this.selectedSensorEvent.id, this.pausedUntil, this.reason || null).then(() => {
        this.flagChanges()
      }).catch((error) => {
        this.errorMessage = this.localizedBackendMessage(error.message)
      })
    },
    confirmUnpause () {
      this.errorMessage = ''
      cdcsensorService.unpauseSensorEvent(this.selectedSensorEvent.id).then(() => {
        this.flagChanges()
      }).catch((error) => {
        this.errorMessage = this.localizedBackendMessage(error.message)
      })
    },
    close () {
      this.errorMessage = ''
      this.showUntilError = false
      this.emitClosed()
      Modal.getInstance(document.getElementById('pauseSensorEventModal'))?.hide()
    }
  }
}
</script>

<style lang="scss" scoped>
.modal {
  .modal-dialog {
    width: 400px;
  }

  .rule-summary {
    font-weight: 500;
    color: #0069d9;
  }

  .preset-bar {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 4px;
  }

  .paused-summary {
    font-size: 0.95em;
  }

  .paused-details {
    margin-top: 6px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
}
</style>
